import { Title, Text, Flex, Button, Loader } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { MessageRole, Sentiment, ToolFunctionType } from 'interfaces/Reporting';
import { useNotification } from 'context/NotificationContext';
import ReportContext from 'context/ReportContext';
import { ReactNode, useContext, useState } from 'react';
import { getHighestSentiment, sentimentLabels } from '../../utils';
import { formatPhoneNumber } from 'utils/phone';
import { downloadAsCSV } from 'utils/file';
import { useDeviceType } from 'hooks/useDeviceType';
import { mergeNames } from 'utils/string';
import { getTimeDiffFromStartTime } from 'utils/date';
import { safeParseToolArgs } from 'utils/json';
import Messages from './Messages';

export type Message = {
  isOwner: boolean;
  time: string;
  speaker: string;
  sentiment: Sentiment | null;
  content: ReactNode;
  action: ToolFunctionType | undefined;
};

const Transcripts = () => {
  const { showSuccess, showError } = useNotification();
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    data: { callDetails, isLoadingCallDetails },
  } = useContext(ReportContext);
  const { isMobile } = useDeviceType();
  const wasDisabled =
    callDetails.messages?.[0] && 'record_transcript' in callDetails.messages[0];
  const isUnavailable = !isLoadingCallDetails && wasDisabled;
  const disableTranscript =
    isDownloading || isLoadingCallDetails || isUnavailable;

  const formatMessages = (): Message[] => {
    if (isUnavailable || !callDetails.messages) {
      return [];
    }
    return callDetails.messages
      .filter(
        (message) =>
          message.role !== MessageRole.SYSTEM &&
          message.role !== MessageRole.TOOL
      )
      .map((message) => {
        const time = getTimeDiffFromStartTime(
          message.timestamp,
          callDetails.start_time
        );
        const isOwner = message.role === MessageRole.USER;
        let speaker = 'Assistant';
        let sentiment = null;
        let content = message.content as ReactNode;
        let action = undefined;

        if (message.role === MessageRole.ASSISTANT) {
          const toolCall = message.tool_calls?.[0];
          action = toolCall?.function.name;
          const functionArgs = toolCall?.function?.arguments;
          
          const {
            first_name,
            last_name,
            phone_number,
            message: actionMessage,
          } = safeParseToolArgs(functionArgs);

          switch (action) {
            case ToolFunctionType.TRANSFER_CALL:
              content = (
                <>
                  <Text fw={500} component="span">
                    Sent to
                  </Text>
                  : {mergeNames(first_name || '', last_name || '')} {phone_number && (
                  <>
                    (
                    <Text fw={400} component="span">
                      {formatPhoneNumber(phone_number)}
                    </Text>
                    )
                  </>
                  )}
                </>
              );
              break;
            case ToolFunctionType.SEND_MESSAGE:
              content = (
                <>
                  {actionMessage && (
                    <Text mb={8} component="span" display="block">
                      <Text fw={500} component="span">
                        Message
                      </Text>
                      : {actionMessage}
                    </Text>
                  )}
                  <Text component="span" display="block">
                    <Text fw={500} component="span">
                      Sent to
                    </Text>
                    : {mergeNames(first_name || '', last_name || '')}
                  </Text>
                </>
              );
              break;
            case ToolFunctionType.END_CONVERSATION:
              break;
            default:
              break;
          }
        } else {
          speaker = formatPhoneNumber(callDetails.participant?.phone_number);
          sentiment = getHighestSentiment(message.sentiment_scores)[0];
        }

        return {
          isOwner,
          time,
          speaker,
          sentiment,
          content,
          action,
        };
      });
  };

  const messages = formatMessages();

  const handleDownloadTranscript = () => {
    try {
      setIsDownloading(true);

      // Format messages for CSV
      const csvData = messages.map(({ time, speaker, content, sentiment }) => ({
        Time: time,
        Speaker: speaker,
        Message: content,
        Sentiment: sentiment ? sentimentLabels[sentiment] : '',
      }));

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `transcript_${callDetails.participant?.phone_number}_${timestamp}.csv`;

      downloadAsCSV(csvData, filename);
      showSuccess('Transcript downloaded successfully');
    } catch (error) {
      console.error('Error downloading transcript:', error);
      showError('Failed to download transcript. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Flex justify="space-between" w="100%" align="center" mb="sm">
        <Title size="md" fw={600}>
          Transcripts
        </Title>
        <Button
          variant="transparent"
          size="sm"
          fw={400}
          p={0}
          disabled={disableTranscript}
          onClick={handleDownloadTranscript}
          style={{ opacity: disableTranscript ? 0.6 : 1 }}
          bg="none"
          rightSection={
            isDownloading ? (
              <Loader size="0.75rem" style={{ marginLeft: '0.7rem' }} />
            ) : (
              <IconDownload
                size={19}
                stroke={1.5}
                style={{ marginLeft: '0.25rem', marginBottom: '0.2rem' }}
              />
            )
          }
        >
          Download {isMobile ? '' : 'Transcript'}
        </Button>
      </Flex>

      <Messages messages={messages} isUnavailable={isUnavailable} />
    </>
  );
};

export default Transcripts;
